import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import { LoadingSpinner, Button } from '../components/Common';
import { DynamicIcon } from '../components/icons';
import { useNotification } from '../context/NotificationContext';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';

const SecuritySettings = ({ token }) => {
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { formatDate } = useTimezoneFormatter();
    const { addNotification } = useNotification();

    const fetchSessions = async () => {
        try {
            const res = await apiFetch('/auth/sessions', token);
            setSessions(res.sessions || []);
        } catch (err) {
            console.error("Failed to fetch sessions:", err);
            addNotification("Could not load active sessions", "error");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, [token]);

    const handleRevoke = async (sessionId) => {
        if (!window.confirm("Are you sure you want to terminate this session?")) return;
        try {
            await apiFetch(`/auth/sessions/${sessionId}`, token, { method: 'DELETE' });
            addNotification("Session terminated successfully");
            fetchSessions();
        } catch (err) {
            addNotification(err.message, "error");
        }
    };

    if (isLoading) return <LoadingSpinner />;

    const currentSid = sessionStorage.getItem("auth_sid");

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                        <DynamicIcon name="Shield" className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Active Sessions</h3>
                        <p className="text-xs text-gray-500">Manage your active login sessions across devices.</p>
                    </div>
                </div>

                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {sessions.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No active sessions found.</div>
                    ) : (
                        sessions.map((session) => (
                            <div key={session.session_id} className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                <div className="flex items-start gap-4">
                                    <div className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-500">
                                        <DynamicIcon name={session.device_info?.toLowerCase().includes('mobile') ? 'Smartphone' : 'Monitor'} className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900 dark:text-gray-100">
                                                {session.device_info || "Unknown Device"}
                                            </span>
                                            {session.session_id === currentSid && (
                                                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold uppercase">
                                                    Current Session
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                <DynamicIcon name="MapPin" className="w-3.5 h-3.5" />
                                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                                    {[session.city, session.country_name].filter(Boolean).join(', ') || session.ip_address || "Unknown Location"}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                <DynamicIcon name="Clock" className="w-3.5 h-3.5" />
                                                <span>Started {formatDate(session.created_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 self-end md:self-center">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-100 dark:border-red-900/30"
                                        onClick={() => handleRevoke(session.session_id)}
                                        disabled={session.session_id === currentSid}
                                    >
                                        Revoke Access
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 flex gap-3">
                <div className="text-blue-600 dark:text-blue-400 mt-0.5">
                    <DynamicIcon name="Info" className="w-5 h-5" />
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-medium mb-1">Security Recommendation</p>
                    <p>If you see a device or location you don't recognize, revoke the session immediately and change your password.</p>
                </div>
            </div>
        </div>
    );
};

export default SecuritySettings;
